import React, { useEffect, useId, useRef, useState } from 'react';
import { Plus } from 'lucide-react';
import {
  EXAM_BLOCK_LABELS,
  EXAM_BLOCK_MODES,
  GAME_MODE_CATALOG,
  createBlockId,
  defaultPayloadForMode,
  guideBlockModesForSubject,
  type EvaluationBlock,
  type EvaluationExam,
  type GameMode,
} from '@interactive-room';

export interface PrintBlockInsertGapProps {
  exam: EvaluationExam;
  /** Índice donde insertar (0 = antes del primero; blocks.length = al final). */
  insertAt: number;
  onInsert: (block: EvaluationBlock, insertAt: number) => void;
}

/**
 * Ranura contextual entre bloques: “+ Antes / Después” sin contaminar el chat.
 */
export function PrintBlockInsertGap({ exam, insertAt, onInsert }: PrintBlockInsertGapProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const isGuide = exam.resourceType === 'guide';
  const modes: GameMode[] = isGuide
    ? guideBlockModesForSubject(exam.subjectTag)
    : [...EXAM_BLOCK_MODES];

  useEffect(() => {
    if (!open) return;
    const onDoc = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const add = (mode: GameMode) => {
    const block: EvaluationBlock = {
      id: createBlockId(),
      index: insertAt,
      mode,
      label: EXAM_BLOCK_LABELS[mode] ?? GAME_MODE_CATALOG[mode].displayName,
      payload: defaultPayloadForMode(mode) as EvaluationBlock['payload'],
    };
    onInsert(block, insertAt);
    setOpen(false);
  };

  const label =
    insertAt <= 0
      ? 'Agregar bloque al inicio'
      : insertAt >= exam.blocks.length
        ? 'Agregar bloque al final'
        : `Agregar bloque entre #${insertAt} y #${insertAt + 1}`;

  return (
    <div
      ref={rootRef}
      className={`print-block-insert no-print${open ? ' print-block-insert--open' : ''}`}
    >
      <button
        type="button"
        className="print-block-insert__trigger"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={label}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="print-block-insert__line" aria-hidden />
        <span className="print-block-insert__chip">
          <Plus className="h-3 w-3" aria-hidden />
          Bloque
        </span>
        <span className="print-block-insert__line" aria-hidden />
      </button>
      {open ? (
        <div id={listId} className="print-block-insert__menu" role="listbox" aria-label={label}>
          {modes.map((mode) => (
            <button
              key={mode}
              type="button"
              role="option"
              className="print-block-insert__option"
              onClick={() => add(mode)}
            >
              {!isGuide ? <span aria-hidden>{GAME_MODE_CATALOG[mode].icon} </span> : null}
              {EXAM_BLOCK_LABELS[mode] ?? GAME_MODE_CATALOG[mode].displayName}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
